import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  COLUMN_TYPE,
  FileData,
  GetValidColumnTypesFunction,
  SpreadsheetColumn,
  TaskState,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  CONCENTRATION_COLUMN_TYPES,
  convertToAutomationColumnType,
  VOLUME_COLUMN_TYPES,
} from 'lab-automation/settings/automation-output-file-config/AutomationOutputFileColumnUtils';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import CreateResultSchemaModal from 'results/assay-schemas/CreateResultSchemaModal';
import {ContainerSchema, PlateSchemaType, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import CreateTagSchemaModal from 'settings/registry/CreateTagSchemaModal';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

import {
  AutomationOutputFileColumnType,
  AutomationOutputFileColumnTypeMetadata,
  AutomationOutputFileConfigSchemaInfoType,
  AutomationOutputFileDestinationInfo,
  AutomationOutputFileEntitySchemaInfo,
  AutomationOutputFileResultSchemaInfo,
  AutomationOutputFileSchemaInfo,
  ClientAutomationOutputFileConfigSpec,
  ColumnTypesByName,
  DirectTransformationFieldInfoWithColumnIndex,
  DirectTransformationFieldInfoWithColumnName,
  labAutomationSetColumnsMappingTypeOptions,
  SchemaFieldColumnTypeMetadata,
  SchemaInfoFields,
  SetColumnsMappingType,
  TransformationFieldInfo,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';
import {DestinationSchemaQuery, DestinationSchemaQueryVariables} from './__generated/DestinationSchemaQuery';
import {EntitySchemaQuery, EntitySchemaQueryVariables} from './__generated/EntitySchemaQuery';
import {ResultSchemaQuery, ResultSchemaQueryVariables} from './__generated/ResultSchemaQuery';
import {convertResultSchemaToDatasetConfigString} from './DatasetConfigConstants';
import ImportColumnHeadersButton from './ImportColumnHeadersButton';
import {findSchemaFieldForKey, isSchemaFieldApiId} from './SchemaInfoFieldKeys';
import {getSetColumnMapErrors, SetColumnsMapError} from './SetColumnsMapErrors';
import SetColumnsMapFieldsSection, {
  _getDestinationInfoSchemaId,
  getColumnTypeMetadataFromSchemaFieldOption,
  getNewEntitySchemaField,
  getNewResultSchemaField,
} from './SetColumnsMapFieldsSection';

export function ActionTypeSelector(props: {
  setColumnsMappingType: SetColumnsMappingType;
  onSelect: (value: SetColumnsMappingType) => void;
}): JSX.Element {
  return (
    <SelectDropdown
      displayName={labAutomationSetColumnsMappingTypeOptions[props.setColumnsMappingType]}
      menu={
        <Menu
          value={props.setColumnsMappingType}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          scrollable={true}
        >
          {Object.entries(labAutomationSetColumnsMappingTypeOptions).map(
            ([value, display]: [SetColumnsMappingType, string]) => (
              <Option key={value} value={value} onSelect={props.onSelect}>
                {display}
              </Option>
            )
          )}
        </Menu>
      }
    />
  );
}

export interface SetColumnsMapModalConfiguration {
  initialOutputFileConfig: ClientAutomationOutputFileConfigSpec;
  initialErrors: Array<SetColumnsMapError>;
  handleUpdatedOutputFileConfig: (outputFileConfig: ClientAutomationOutputFileConfigSpec) => void;
  getValidColumnTypes: GetValidColumnTypesFunction;
  goToUploadSpreadsheet: () => void;
  goToImportConnectionFiles: () => void;
  ownerId: string;
}

export interface SetColumnsMapModalProps extends SetColumnsMapModalConfiguration {
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  uploadedFileData: FileData | null;
}

// export for test
export function _getSchemaInfoFields(
  outputFileConfig: ClientAutomationOutputFileConfigSpec
): SchemaInfoFields {
  return {
    ...outputFileConfig.destinationInfo?.fields,
    ...outputFileConfig.entitySchemaInfo?.fields,
    ...outputFileConfig.resultSchemaInfo?.fields,
  };
}

interface CreateSchemaModalProps {
  onRequestSubmit: (schema: Schema) => void;
  onRequestClose: () => void;
  newFieldNames: Array<string>;
  ownerId: string;
}

interface DirectSchemaMappingByIndex {
  [index: string | number]: {
    schemaField: string;
    schemaFieldKey?: string;
    schemaInfoType: AutomationOutputFileConfigSchemaInfoType;
    schemaId: string | null;
    isFieldRequired: boolean;
  };
}

/**
 * `otherSchemas` are schemas besides `schema` whose fields `schemaInfo` can refer to, such as a plate destination's
 * well schema. A field is looked up on them only by API ID, since a field name refers to `schema`'s field.
 */
function _getDirectSchemaMappingHelper(
  schemaInfo: AutomationOutputFileSchemaInfo | null | undefined,
  schemaInfoType: AutomationOutputFileConfigSchemaInfoType,
  schemaId: string | null,
  schema: Schema | null,
  otherSchemas: Array<Schema | null> = []
): DirectSchemaMappingByIndex {
  const directSchemaMappingByIndex: DirectSchemaMappingByIndex = {};
  if (!schemaInfo) {
    return directSchemaMappingByIndex;
  }
  Object.entries(schemaInfo.fields).forEach(([fieldKey, transformationFieldInfo]) => {
    if (transformationFieldInfo.type !== TransformationFieldType.DIRECT) {
      return;
    }
    let key: string | number;
    const transformationWithColumnName =
      transformationFieldInfo as DirectTransformationFieldInfoWithColumnName;
    const transformationWithColumnIndex =
      transformationFieldInfo as DirectTransformationFieldInfoWithColumnIndex;
    if (transformationWithColumnName.columnName) {
      key = transformationWithColumnName.columnName;
    } else {
      key = transformationWithColumnIndex.columnIndex!;
    }
    let schemaField = fieldKey;
    let fieldSchemaId = schemaId;
    let isFieldRequired = true;
    if (schemaId != null) {
      const match =
        findSchemaFieldForKey(fieldKey, [schema]) ??
        (isSchemaFieldApiId(fieldKey) ? findSchemaFieldForKey(fieldKey, otherSchemas) : null);
      if (match) {
        schemaField = match.field.name;
        fieldSchemaId = match.schema.id;
        isFieldRequired = match.field.isRequired;
      }
    }
    directSchemaMappingByIndex[key] = {
      schemaField,
      schemaFieldKey: fieldKey,
      schemaInfoType,
      schemaId: fieldSchemaId,
      isFieldRequired,
    };
  });
  return directSchemaMappingByIndex;
}

// export for test
export function _getDirectSchemaMapping(
  destinationInfo: AutomationOutputFileDestinationInfo | undefined | null,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | undefined | null,
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | undefined | null,
  destinationSchema: StorableSchema | null,
  wellContainerSchema: ContainerSchema | null,
  entitySchema: TagSchema | null,
  resultSchema: ResultSchema | null,
  shouldPerformPlateTransfer: boolean | null,
  gridContainerSchema: ContainerSchema | null = null
): DirectSchemaMappingByIndex {
  let schemaMappings = {
    ..._getDirectSchemaMappingHelper(
      entitySchemaInfo,
      AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO,
      entitySchemaInfo?.schema ?? null,
      entitySchema
    ),
    ..._getDirectSchemaMappingHelper(
      resultSchemaInfo,
      AutomationOutputFileConfigSchemaInfoType.RESULT_SCHEMA_INFO,
      resultSchemaInfo?.schema ?? null,
      resultSchema
    ),
  };

  if (shouldPerformPlateTransfer) {
    schemaMappings = {
      ...schemaMappings,
      ..._getDirectSchemaMappingHelper(
        destinationInfo,
        AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO,
        wellContainerSchema ? wellContainerSchema.id : null,
        wellContainerSchema,
        [destinationSchema]
      ),
    };
  } else {
    schemaMappings = {
      ...schemaMappings,
      ..._getDirectSchemaMappingHelper(
        destinationInfo,
        AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO,
        destinationInfo ? _getDestinationInfoSchemaId(destinationInfo) : null,
        destinationSchema,
        [gridContainerSchema]
      ),
    };
  }
  return schemaMappings;
}

// export for test
export function _getColumnTypeMetadatas(
  outputFileConfig: ClientAutomationOutputFileConfigSpec,
  directSchemaMappingByIndex: DirectSchemaMappingByIndex
): Array<AutomationOutputFileColumnTypeMetadata> {
  const columnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata> = [];
  if (outputFileConfig.columnTypes) {
    outputFileConfig.columnTypes.forEach((columnType, index) => {
      if (directSchemaMappingByIndex.hasOwnProperty(index)) {
        columnTypeMetadatas.push({
          columnType,
          name: '',
          ...directSchemaMappingByIndex[index],
          isRequired: true,
        });
      } else if (
        columnType === AutomationOutputFileColumnType.CUSTOM_NOTATION &&
        'customSyntaxId' in outputFileConfig
      ) {
        columnTypeMetadatas.push({
          columnType,
          name: '',
          customSyntaxId: outputFileConfig.customSyntaxId,
          isRequired: true,
        });
      } else {
        columnTypeMetadatas.push({columnType, name: '', isRequired: true});
      }
    });
  } else {
    Object.entries(outputFileConfig.columnTypesByName).forEach(([key, value]) => {
      if (directSchemaMappingByIndex.hasOwnProperty(key)) {
        columnTypeMetadatas.push({
          columnType: value.columnType,
          isRequired: value.isRequired ?? true,
          name: key,
          ...directSchemaMappingByIndex[key],
        });
      } else if (
        value.columnType === AutomationOutputFileColumnType.CUSTOM_NOTATION &&
        'customSyntaxId' in outputFileConfig
      ) {
        columnTypeMetadatas.push({
          columnType: value.columnType,
          name: key,
          customSyntaxId: outputFileConfig.customSyntaxId,
          isRequired: value.isRequired ?? true,
        });
      } else {
        columnTypeMetadatas.push({
          columnType: value.columnType,
          name: key,
          isRequired: value.isRequired ?? true,
        });
      }
    });
  }
  return columnTypeMetadatas;
}

function uploadedFileColumnsToMetadata(
  uploadedFileData: FileData,
  destinationInfo: AutomationOutputFileDestinationInfo | null | undefined,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | null | undefined,
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | null | undefined
): Array<AutomationOutputFileColumnTypeMetadata> {
  return (
    uploadedFileData.columns?.map((column) =>
      spreadsheetColumnToMetadata(column, destinationInfo, entitySchemaInfo, resultSchemaInfo)
    ) || []
  );
}

function spreadsheetColumnToMetadata(
  spreadsheetColumn: SpreadsheetColumn,
  destinationInfo: AutomationOutputFileDestinationInfo | null | undefined,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | null | undefined,
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | null | undefined
): AutomationOutputFileColumnTypeMetadata {
  const columnType = convertToAutomationColumnType(spreadsheetColumn.type);

  if (columnType === AutomationOutputFileColumnType.IGNORE) {
    let fieldOption = null;
    if (resultSchemaInfo && resultSchemaInfo.schema == null && !entitySchemaInfo) {
      fieldOption = getNewResultSchemaField();
    } else if (entitySchemaInfo && entitySchemaInfo.schema == null && !resultSchemaInfo) {
      fieldOption = getNewEntitySchemaField();
    }
    if (fieldOption != null) {
      return getColumnTypeMetadataFromSchemaFieldOption(fieldOption, spreadsheetColumn.name, true);
    }
  }

  if (columnType === AutomationOutputFileColumnType.CUSTOM_NOTATION) {
    return {columnType, name: spreadsheetColumn.name, customSyntaxId: spreadsheetColumn.customSyntaxId};
  }

  const schemaInfoType = getSchemaInfoType(spreadsheetColumn);
  if (schemaInfoType == null) {
    return {columnType, name: spreadsheetColumn.name};
  }
  let schemaId: string | null = null;
  if (schemaInfoType === AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO) {
    schemaId = destinationInfo ? _getDestinationInfoSchemaId(destinationInfo) : null;
  } else if (schemaInfoType === AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO) {
    schemaId = entitySchemaInfo?.schema ?? null;
  } else {
    schemaId = resultSchemaInfo?.schema ?? null;
  }
  const col = {
    name: spreadsheetColumn.name,
    schemaInfoType,
    schemaField: spreadsheetColumn.schemaField!.name,
    schemaFieldKey: spreadsheetColumn.schemaField!.apiId || undefined,
    schemaId,
    columnType,
    isRequired: true,
    isFieldRequired: spreadsheetColumn.schemaField!.isRequired,
  };

  return col;
}

function getSchemaInfoType(
  spreadsheetColumn: SpreadsheetColumn
): AutomationOutputFileConfigSchemaInfoType | undefined {
  switch (spreadsheetColumn.type) {
    case COLUMN_TYPE.BOX_SCHEMA_FIELD:
    case COLUMN_TYPE.CONTAINER_SCHEMA_FIELD:
    case COLUMN_TYPE.PLATE_SCHEMA_FIELD:
      return AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO;
    case COLUMN_TYPE.RESULT_SCHEMA_FIELD:
      return AutomationOutputFileConfigSchemaInfoType.RESULT_SCHEMA_INFO;
    case COLUMN_TYPE.SCHEMA_FIELD:
      return AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO;
    default:
      return undefined;
  }
}

function _getSetColumnsMappingType(
  outputFileConfig: ClientAutomationOutputFileConfigSpec
): SetColumnsMappingType {
  if (outputFileConfig.columnTypes) {
    return SetColumnsMappingType.BY_COLUMN_ORDER;
  }
  return SetColumnsMappingType.BY_COLUMN_NAME;
}

function _convertToColumnTypesByName(
  columnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata>
): ColumnTypesByName {
  const columnTypesByName: ColumnTypesByName = {};
  columnTypeMetadatas.forEach((columnTypeMetadata) => {
    if (columnTypeMetadata.columnType && columnTypeMetadata.name) {
      columnTypesByName[columnTypeMetadata.name] = {
        columnType: columnTypeMetadata.columnType,
        isRequired: columnTypeMetadata.isRequired ?? true,
      };
    }
  });
  return columnTypesByName;
}

function _convertToColumnTypes(
  columnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata>
): Array<AutomationOutputFileColumnType> {
  const columnTypes: Array<AutomationOutputFileColumnType> = [];
  columnTypeMetadatas.forEach((columnTypeMetadata) => {
    if (columnTypeMetadata.columnType) {
      columnTypes.push(columnTypeMetadata.columnType);
    }
  });
  return columnTypes;
}

function getDirectTransformationFieldInfo(
  setColumnsMappingType: SetColumnsMappingType,
  columnTypeMetadata?: AutomationOutputFileColumnTypeMetadata & {index: number}
): TransformationFieldInfo {
  if (setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_NAME) {
    return {
      type: TransformationFieldType.DIRECT,
      columnName: columnTypeMetadata?.name,
    };
  } else {
    return {
      type: TransformationFieldType.DIRECT,
      columnIndex: columnTypeMetadata?.index,
    };
  }
}

// export for test
export function _createOutputFileConfig(
  initialOutputFileConfig: ClientAutomationOutputFileConfigSpec,
  columnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata>,
  setColumnsMappingType: SetColumnsMappingType,
  volumeUnits: string,
  concentrationUnits: string,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | undefined | null,
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | undefined | null
): ClientAutomationOutputFileConfigSpec {
  const outputFileConfig = {
    ...initialOutputFileConfig,
    volumeUnits: volumeUnits || undefined,
    concentrationUnits: concentrationUnits || undefined,
  };
  const columnTypeMetadatasWithIndex = columnTypeMetadatas.map((columnTypeMetadata, i) => ({
    ...columnTypeMetadata,
    index: i,
  }));

  const columnsWithCustomSyntax = columnTypeMetadatas.filter((metadata) =>
    metadata.hasOwnProperty('customSyntaxId')
  );
  if (columnsWithCustomSyntax.length > 0) {
    outputFileConfig.customSyntaxId = (
      columnsWithCustomSyntax[0] as {customSyntaxId: string}
    ).customSyntaxId!;
  }

  if (outputFileConfig.destinationInfo) {
    outputFileConfig.destinationInfo = {
      ...outputFileConfig.destinationInfo,
      fields: updatedSchemaFieldsHelper(
        outputFileConfig.destinationInfo.fields,
        AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO,
        columnTypeMetadatasWithIndex,
        setColumnsMappingType
      ),
    };
  }
  if (entitySchemaInfo) {
    outputFileConfig.entitySchemaInfo = {
      ...entitySchemaInfo,
      fields: updatedSchemaFieldsHelper(
        entitySchemaInfo.fields,
        AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO,
        columnTypeMetadatasWithIndex,
        setColumnsMappingType
      ),
    };
  }
  if (resultSchemaInfo) {
    outputFileConfig.resultSchemaInfo = {
      ...resultSchemaInfo,
      fields: updatedSchemaFieldsHelper(
        resultSchemaInfo.fields,
        AutomationOutputFileConfigSchemaInfoType.RESULT_SCHEMA_INFO,
        columnTypeMetadatasWithIndex,
        setColumnsMappingType
      ),
    };
  }

  if (setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_ORDER) {
    return {
      ...outputFileConfig,
      columnTypes: _convertToColumnTypes(columnTypeMetadatas),
      columnTypesByName: undefined,
    };
  } else if (setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_NAME) {
    return {
      ...outputFileConfig,
      columnTypes: undefined,
      columnTypesByName: _convertToColumnTypesByName(columnTypeMetadatas),
    };
  }
  return outputFileConfig;
}

function updatedSchemaFieldsHelper(
  schemaFields: SchemaInfoFields,
  schemaInfoType: AutomationOutputFileConfigSchemaInfoType,
  columnTypeMetadatasWithIndex: Array<AutomationOutputFileColumnTypeMetadata & {index: number}>,
  setColumnsMappingType: SetColumnsMappingType
): SchemaInfoFields {
  const columnsForSchemaType = columnTypeMetadatasWithIndex.filter(
    (column) => column.schemaInfoType === schemaInfoType && column.schemaField != null
  ) as Array<SchemaFieldColumnTypeMetadata & {index: number}>;

  const updatedSchemaFields: SchemaInfoFields = {};

  for (const [fieldName, transformationFieldInfo] of Object.entries(schemaFields)) {
    if (transformationFieldInfo.type !== TransformationFieldType.DIRECT) {
      updatedSchemaFields[fieldName] = transformationFieldInfo;
    }
  }

  for (const columnMetadata of columnsForSchemaType) {
    // A field keeps the key it was loaded with, so saving can't move it to another schema's field of the same name.
    updatedSchemaFields[columnMetadata.schemaFieldKey ?? columnMetadata.schemaField] =
      getDirectTransformationFieldInfo(setColumnsMappingType, columnMetadata);
  }
  return updatedSchemaFields;
}

function getMatchingFieldByName(fields: Array<SchemaField>, fieldName: string): SchemaField | null {
  for (const schemaField of fields) {
    if (schemaField.displayName === fieldName) {
      return schemaField;
    }
  }
  return null;
}

// export for test
export function _getCurrentVisibleErrors(
  errors: Array<SetColumnsMapError>,
  currentErrors: Array<SetColumnsMapError>
): Array<SetColumnsMapError> {
  return currentErrors.filter((currentError) =>
    errors.map((error) => error.type).some((errorType) => errorType === currentError.type)
  );
}

export const DESTINATION_SCHEMA_QUERY = gql`
  query DestinationSchemaQuery($storableSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    storableSchema(id: $storableSchemaId) {
      ... on StorableSchemaInterface {
        ...StorableSchemaGraphQL
        ... on PlateSchemaNode {
          ...PlateSchemaGraphQL
          containerSchema {
            id
            ...ContainerSchemaGraphQL
          }
        }
        ... on BoxSchemaNode {
          id
          containerSchema {
            id
            ...ContainerSchemaGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StorableSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('PlateSchema').fragment}
  ${getFragmentWrapper('ContainerSchema').fragment}
`;

export const RESULT_SCHEMA_QUERY = gql`
  query ResultSchemaQuery($resultSchemaId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    resultSchema(id: $resultSchemaId) {
      ... on ResultSchemaNode {
        ...ResultSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ResultSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ENTITY_SCHEMA_QUERY = gql`
  query EntitySchemaQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        ...TagSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function SetColumnsMapModal(props: SetColumnsMapModalProps): JSX.Element {
  const [entitySchemaId, setEntitySchemaId] = useState(
    props.initialOutputFileConfig.entitySchemaInfo?.schema ?? null
  );
  const [resultSchemaId, setResultSchemaId] = useState(
    props.initialOutputFileConfig.resultSchemaInfo?.schema ?? null
  );

  const entitySchemaResponse = useSkippableBenchlingQuery<EntitySchemaQuery, EntitySchemaQueryVariables>(
    ENTITY_SCHEMA_QUERY,
    {
      variables: {tagSchemaId: entitySchemaId!},
      skip: !entitySchemaId,
    }
  );
  let entitySchema: TagSchema | null = null;
  if (entitySchemaResponse.type === QueryResultType.SUCCESS) {
    const schemaGql = entitySchemaResponse.data.tagSchema;
    entitySchema = !isInaccessible(schemaGql) ? graphQLToREST('TagSchema', schemaGql) : null;
  }

  const resultSchemaResponse = useSkippableBenchlingQuery<ResultSchemaQuery, ResultSchemaQueryVariables>(
    RESULT_SCHEMA_QUERY,
    {
      variables: {resultSchemaId: resultSchemaId!},
      skip: !resultSchemaId,
    }
  );
  let resultSchema: ResultSchema | null = null;
  if (resultSchemaResponse.type === QueryResultType.SUCCESS) {
    const schemaGql = resultSchemaResponse.data.resultSchema;
    resultSchema = schemaGql && !isInaccessible(schemaGql) ? graphQLToREST('ResultSchema', schemaGql) : null;
  }

  const destinationSchemaId = props.initialOutputFileConfig.destinationInfo
    ? _getDestinationInfoSchemaId(props.initialOutputFileConfig.destinationInfo)
    : null;
  const destinationSchemaResponse = useSkippableBenchlingQuery<
    DestinationSchemaQuery,
    DestinationSchemaQueryVariables
  >(DESTINATION_SCHEMA_QUERY, {
    variables: {storableSchemaId: destinationSchemaId!},
    skip: !destinationSchemaId,
  });
  let destinationSchema: StorableSchema | null = null;
  let wellContainerSchema: ContainerSchema | null = null;
  // A plate's or box's mapped fields can be on its containers' schema, even when its containers aren't the
  // destination.
  let gridContainerSchema: ContainerSchema | null = null;
  if (destinationSchemaResponse.type === QueryResultType.SUCCESS) {
    const schemaGql = destinationSchemaResponse.data.storableSchema;
    destinationSchema = !isInaccessible(schemaGql) ? graphQLToREST('StorableSchema', schemaGql) : null;
    if (
      destinationSchema &&
      (schemaGql.__typename === 'PlateSchemaNode' || schemaGql.__typename === 'BoxSchemaNode')
    ) {
      const containerSchemaGql = schemaGql.containerSchema;
      if (containerSchemaGql && !isInaccessible(containerSchemaGql)) {
        gridContainerSchema = graphQLToREST('ContainerSchema', containerSchemaGql);
      }
      if (
        props.initialOutputFileConfig.shouldPerformPlateTransfer &&
        schemaGql.__typename === 'PlateSchemaNode' &&
        schemaGql.plateSchemaType &&
        schemaGql.plateSchemaType === PlateSchemaType.FIXED_PLATE
      ) {
        wellContainerSchema = gridContainerSchema;
      }
    }
  }
  const entitySchemaInfo = props.initialOutputFileConfig.entitySchemaInfo
    ? {
        ...props.initialOutputFileConfig.entitySchemaInfo,
        schema: entitySchemaId,
      }
    : null;
  const resultSchemaInfo = props.initialOutputFileConfig.resultSchemaInfo
    ? {
        ...props.initialOutputFileConfig.resultSchemaInfo,
        schema: resultSchemaId,
      }
    : null;
  const initialSetColumnsMappingType: SetColumnsMappingType = _getSetColumnsMappingType(
    props.initialOutputFileConfig
  );
  const initialColumnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata> = _getColumnTypeMetadatas(
    props.initialOutputFileConfig,
    _getDirectSchemaMapping(
      props.initialOutputFileConfig.destinationInfo,
      entitySchemaInfo,
      resultSchemaInfo,
      destinationSchema,
      wellContainerSchema,
      entitySchema,
      resultSchema,
      props.initialOutputFileConfig.shouldPerformPlateTransfer,
      gridContainerSchema
    )
  );
  const initialVolumeUnits: string = props.initialOutputFileConfig.volumeUnits
    ? props.initialOutputFileConfig.volumeUnits
    : '';
  const initialConcentrationUnits: string = props.initialOutputFileConfig.concentrationUnits
    ? props.initialOutputFileConfig.concentrationUnits
    : '';

  const [setColumnsMappingType, setSetColumnsMappingType] = useState(initialSetColumnsMappingType);
  const [columnTypeMetadatas, setColumnTypeMetadatas] = useState(initialColumnTypeMetadatas);
  const [volumeUnits, setVolumeUnits] = useState(initialVolumeUnits);
  const [concentrationUnits, setConcentrationUnits] = useState(initialConcentrationUnits);
  const [errors, setErrors] = useState(props.initialErrors);

  // Make sure we only pull in columns based on spreadsheet upload once per file
  const importedFileColumns = useRef(false);

  const areSchemasLoading = [entitySchemaResponse, resultSchemaResponse, destinationSchemaResponse].some(
    (queryResponse) => queryResponse.type === QueryResultType.PENDING
  );
  // Mappings are resolved against their schemas (a field key can be an API ID, and a field can be on a container
  // schema), so when the schemas weren't cached, rebuild them once the schemas first load. Nothing can be edited
  // before then, since the fields aren't shown while the schemas load.
  const haveSchemasLoaded = useRef(!areSchemasLoading);
  useEffect(() => {
    if (areSchemasLoading || haveSchemasLoaded.current) {
      return;
    }
    haveSchemasLoaded.current = true;
    if (!importedFileColumns.current) {
      setColumnTypeMetadatas(initialColumnTypeMetadatas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areSchemasLoading]);
  // Copy column guesses from a file upload and also switch to mapping by column name, to take advantage of
  // named headers from a spreadsheet file.
  useEffect(() => {
    if (props.uploadedFileData?.state === TaskState.COMPLETED && !importedFileColumns.current) {
      importedFileColumns.current = true;
      setSetColumnsMappingType(SetColumnsMappingType.BY_COLUMN_NAME);
      setColumnTypeMetadatas(
        uploadedFileColumnsToMetadata(
          props.uploadedFileData,
          props.initialOutputFileConfig.destinationInfo,
          entitySchemaInfo,
          resultSchemaInfo
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.uploadedFileData]);

  const createOutputFileConfig = (): ClientAutomationOutputFileConfigSpec =>
    _createOutputFileConfig(
      props.initialOutputFileConfig,
      columnTypeMetadatas,
      setColumnsMappingType,
      volumeUnits,
      concentrationUnits,
      entitySchemaInfo,
      resultSchemaInfo
    );

  const currentErrors: Array<SetColumnsMapError> = getSetColumnMapErrors(
    columnTypeMetadatas,
    setColumnsMappingType,
    volumeUnits,
    concentrationUnits,
    props.initialOutputFileConfig.actionType,
    _getSchemaInfoFields(createOutputFileConfig()),
    entitySchemaInfo,
    props.initialOutputFileConfig.shouldPerformPlateTransfer
  );
  const currentVisibleErrors = _getCurrentVisibleErrors(errors, currentErrors);

  // Example file upload will overwrite existing columns, so is only allowed when column mapping is empty
  function shouldShowUploadButton(): boolean {
    return (
      BenchlingConfig.LAB_AUTOMATION_OUTPUT_FILE_CONFIG_UI_SPREADSHEET_UPLOAD &&
      columnTypeMetadatas.length === 0
    );
  }

  function fileImportPending(): boolean {
    return props.uploadedFileData?.state === TaskState.STARTED;
  }

  function createSchemaModalButton(
    schemaInfoType: AutomationOutputFileConfigSchemaInfoType,
    buttonText: string,
    createSchemaModal: React.ComponentType<CreateSchemaModalProps>,
    updateSchemaId: (schemaId: string) => void
  ): ModalButton {
    const newFields: Array<string> = columnTypeMetadatas
      .filter((metadata) => metadata.schemaId == null && metadata.schemaInfoType === schemaInfoType)
      .map((metadata) => metadata.name ?? '');
    return {
      style: ButtonStyle.SECONDARY,
      submit: false,
      disabled: fileImportPending(),
      text: buttonText,
      onClick: async () => {
        const schema = await legacyRunModal<CreateSchemaModalProps>(createSchemaModal, {
          newFieldNames: newFields,
          ownerId: props.ownerId,
        });
        if (schema != null) {
          const newColumnTypeMetadatas = columnTypeMetadatas.map((metadata) => {
            if (metadata.schemaId == null && metadata.schemaInfoType === schemaInfoType) {
              const matchingSchemaField = getMatchingFieldByName(schema.fields, metadata.name ?? '');
              if (matchingSchemaField != null) {
                return {
                  ...metadata,
                  schemaField: matchingSchemaField.name,
                  schemaFieldKey: matchingSchemaField.apiId || undefined,
                  schemaId: schema.id,
                  isFieldRequired: matchingSchemaField.isRequired,
                  isRequired: matchingSchemaField.isRequired ? true : metadata.isRequired,
                };
              }
            }
            return {...metadata};
          });
          setColumnTypeMetadatas(newColumnTypeMetadatas);
          updateSchemaId(schema.id);
        }
      },
    };
  }

  const modalButtons = [];
  if (resultSchemaInfo != null && resultSchemaInfo.schema == null) {
    modalButtons.push(
      createSchemaModalButton(
        AutomationOutputFileConfigSchemaInfoType.RESULT_SCHEMA_INFO,
        'Create result schema',
        CreateResultSchemaModal,
        setResultSchemaId
      )
    );
  }
  if (entitySchemaInfo != null && entitySchemaInfo.schema == null) {
    modalButtons.push(
      createSchemaModalButton(
        AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO,
        'Create entity schema',
        CreateTagSchemaModal,
        setEntitySchemaId
      )
    );
  }
  modalButtons.push({
    style: ButtonStyle.SECONDARY,
    submit: true,
    disabled: currentVisibleErrors.length > 0 || fileImportPending(),
    text: 'Done',
    dataTestAttrs: {
      component: 'SetColumnsModal',
      element: 'action',
    },
  });

  return (
    <Modal
      buttons={modalButtons}
      {...props.modalProps}
      onRequestSubmit={() => {
        let newOutputFileConfig = createOutputFileConfig();

        // If we're creating a dataset and results, we need to regenerate the dataset mapping
        if (props.initialOutputFileConfig.shouldCreateDataset && resultSchema) {
          newOutputFileConfig = {
            ...newOutputFileConfig,
            datasetConfigJson: convertResultSchemaToDatasetConfigString(resultSchema),
          };
        }

        const newErrors: Array<SetColumnsMapError> = getSetColumnMapErrors(
          columnTypeMetadatas,
          setColumnsMappingType,
          volumeUnits,
          concentrationUnits,
          props.initialOutputFileConfig.actionType,
          _getSchemaInfoFields(newOutputFileConfig),
          entitySchemaInfo,
          props.initialOutputFileConfig.shouldPerformPlateTransfer
        );
        if (newErrors.length > 0) {
          setErrors(newErrors);
          return;
        }
        props.handleUpdatedOutputFileConfig(newOutputFileConfig);
        // Close the modal manually, since SpreadsheetBulkImportModal loses the nice wrapping
        // of onRequestSubmit from legacyRunModal
        props.modalProps.onRequestClose();
      }}
    >
      {areSchemasLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div>
            <FieldLabel label="Mapping type" labelStyle={LabelStyle.LABEL} isRequired={true} />
            <br />
            <div>
              <div className={css(styles.actionTypeSelector)}>
                <ActionTypeSelector
                  setColumnsMappingType={setColumnsMappingType}
                  onSelect={(value) => {
                    setSetColumnsMappingType(value);
                  }}
                />
              </div>
              {shouldShowUploadButton() && (
                <ImportColumnHeadersButton
                  goToUploadSpreadsheet={props.goToUploadSpreadsheet}
                  goToImportConnectionFiles={
                    props.initialOutputFileConfig.transformDataType
                      ? props.goToImportConnectionFiles
                      : undefined
                  }
                />
              )}
            </div>
          </div>
          <br />
          {fileImportPending() ? (
            <CenteredHorizontalThrobber />
          ) : (
            <SetColumnsMapFieldsSection
              columnTypeMetadatas={columnTypeMetadatas}
              setColumnsMappingType={setColumnsMappingType}
              volumeUnits={volumeUnits}
              concentrationUnits={concentrationUnits}
              destinationInfo={props.initialOutputFileConfig.destinationInfo}
              destinationSchema={destinationSchema}
              wellContainerSchema={wellContainerSchema}
              entitySchemaInfo={entitySchemaInfo}
              entitySchema={entitySchema}
              resultSchemaInfo={resultSchemaInfo}
              resultSchema={resultSchema}
              actionType={props.initialOutputFileConfig.actionType}
              shouldPerformPlateTransfer={props.initialOutputFileConfig.shouldPerformPlateTransfer}
              onAddColumnTypeMetadata={(columnTypeMetadata) => {
                setColumnTypeMetadatas([...columnTypeMetadatas, columnTypeMetadata]);
              }}
              onUpdateColumnTypeMetadata={(index, updatedColumnTypeMetadata) => {
                setColumnTypeMetadatas([
                  ...columnTypeMetadatas.slice(0, index),
                  updatedColumnTypeMetadata,
                  ...columnTypeMetadatas.slice(index + 1),
                ]);
              }}
              onUpdateColumnTypeMetadatas={(updatedColumnTypeMetadatas) => {
                setColumnTypeMetadatas(updatedColumnTypeMetadatas);
              }}
              onRemoveColumnTypeMetadata={(index) => {
                setColumnTypeMetadatas([
                  ...columnTypeMetadatas.slice(0, index),
                  ...columnTypeMetadatas.slice(index + 1),
                ]);
              }}
              onUpdateAmount={(columnType, unit) => {
                if (CONCENTRATION_COLUMN_TYPES.has(columnType)) {
                  setConcentrationUnits(unit);
                } else if (VOLUME_COLUMN_TYPES.has(columnType)) {
                  setVolumeUnits(unit);
                }
              }}
            />
          )}

          {currentVisibleErrors.map((error, index) => (
            <div key={index} className="u-small-spacing-bottom">
              <InlineBanner key={`error-${index}`} bannerType={InlineBannerType.ERROR} text={error.message} />
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  actionTypeSelector: {
    marginRight: '21px',
    display: 'inline-block',
  },

  schemaActionButtons: {
    display: 'flex',
    flex: 0.2,
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
  schemaDisplayContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginLeft: '-8px',
    alignItems: 'flex-start',
  },
  schemaName: {
    wordWrap: 'break-word',
    overflowWrap: 'anywhere',
    paddingLeft: '8px',
    paddingTop: '6px',
    paddingBottom: '6px',
    flex: 0.8,
  },
  hidden: {
    display: 'none',
  },
  schemaInfoSection: {
    flex: 0.33,
    marginRight: '32px',
  },
});
